import { useState, useEffect } from "react";

/**
 * `useState` variant that mirrors its value into `localStorage[storageKey]`
 * on every change and rehydrates on mount.
 *
 * Handles corrupted/missing values by falling back to `initialValue`, and
 * quietly no-ops in SSR / private-mode contexts (empty catch on quota errors).
 *
 * Usage:
 *   const [filter, setFilter] = usePersistedState("sos-filter-workers", "active");
 */
export function usePersistedState(storageKey, initialValue) {
  const [value, setValue] = useState(() => {
    if (typeof window === "undefined" || !storageKey) return initialValue;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw !== null) return JSON.parse(raw);
    } catch { /* ignore corrupted json */ }
    return initialValue;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch { /* private mode or quota */ }
  }, [storageKey, value]);

  return [value, setValue];
}
